import { StickyNote2 as DemandIcon, TextSnippet as SolicitationIcon } from '@mui/icons-material';
import { IconButton, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import Paper from '@mui/material/Paper';
import { useEffect, useState } from 'react';
import { IDemandViewModel, ISolicitationViewModel } from 'viewModels';
import { ReactComponent as SeeMoreIcon } from '../../assets/icons/icon-view.svg';
import { ModalDetailsService } from '../Modals/ModalDetailsSolicitation';
import { Container, Content } from './styles';
import TitlePage from '../TitlePage';
import JoyrideTour from '../JoyrideTour';
import { feedSolicitationsSteps } from '../../constants/Tour/Steps/feedSolicitationsSteps';
import { mockSolicitation } from '../../constants/Tour/Mocks/solicitationMock';
import { TutorialType } from '../../types/enums';
import { useAuth } from '../../context';

interface FeedMyServicesProps {
  type: 'demand' | 'solicitation';
  services: ISolicitationViewModel[] | IDemandViewModel[];
}
export function FeedMyServices({ type, services }: FeedMyServicesProps) {
  const { user } = useAuth();
  const [displayModalDetailsEvents, setDisplayModalDetailsEvents] = useState(false);
  const [runningTutorial, setRunningTutorial] = useState(false);
  const [currentServices, setCurrentServices] = useState<
    ISolicitationViewModel[] | IDemandViewModel[]
  >(services);

  const [selectedService, setSelectedService] = useState<
    ISolicitationViewModel | IDemandViewModel
  >();
  const verifyExpires = (date: Date) => {
    const today = new Date();
    const dateLimit = new Date(date);
    const diff = dateLimit.getTime() - today.getTime();
    const diffDays = Math.floor(diff / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  useEffect(() => {
    if (runningTutorial) {
      setCurrentServices([mockSolicitation]);
    } else setCurrentServices(services);
  }, [services, runningTutorial]);

  const handleTutorialStep = (target: string) => {
    if (target === '#solicitation-modal') {
      setSelectedService(mockSolicitation);
      setDisplayModalDetailsEvents(true);
    }
  };

  useEffect(() => {
    if (!user?.tutorialProgress?.some((p) => p === TutorialType.FEED_MY_SERVICES)) {
      setRunningTutorial(true);
    }
  }, [user.tutorialProgress]);

  return (
    <Container>
      <JoyrideTour
        steps={feedSolicitationsSteps}
        type={TutorialType.FEED_MY_SERVICES}
        run={runningTutorial}
        onCallbackFinished={() => {
          setRunningTutorial(false);
          setDisplayModalDetailsEvents(false);
        }}
        onStepChange={handleTutorialStep}
      />
      <Content>
        <div className="header">
          <TitlePage
            title={type === 'demand' ? 'Minhas Demandas' : 'Minhas Solicitações'}
            onRequestOpen={() => {
              setRunningTutorial(true);
            }}
            id="start-item"
          />
        </div>
        <TableContainer className="table" component={Paper}>
          <Table sx={{ minWidth: 650, maxHeight: '15rem' }} size="small" aria-label="a dense table">
            <TableBody>
              {currentServices
                .sort((a, b) => new Date(a.dateLimit).getTime() - new Date(b.dateLimit).getTime())
                .map((service) => (
                  <TableRow
                    key={service.id}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell className="icon-demands" component="th" scope="row">
                      {type === 'demand' ? <DemandIcon /> : <SolicitationIcon />}
                    </TableCell>
                    <TableCell>
                      <p>{service.correspondence.name}</p>
                    </TableCell>
                    <TableCell className="services">
                      <div>
                        <p>{service.specialty?.name ?? '-'}</p>
                        <p>{`${service.address?.city ?? '-'} - ${service.address?.uf ?? '-'}`}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="expires">
                          {verifyExpires(service.dateLimit) < 0 ? (
                            <p>
                              Expirado há{' '}
                              <span className="expired">
                                {Math.abs(verifyExpires(service.dateLimit))} dias
                              </span>
                            </p>
                          ) : (
                            <p>
                              Expira em <span>{verifyExpires(service.dateLimit)} dias</span>
                            </p>
                          )}
                        </div>
                        <p>
                          {new Date(service.dateLimit).toLocaleDateString('pt-BR', {
                            year: 'numeric',
                            month: 'numeric',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: 'numeric',
                          })}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="action">
                      <IconButton
                        id="expand-icon"
                        onClick={() => {
                          setSelectedService(service);
                          setDisplayModalDetailsEvents(true);
                        }}>
                        <SeeMoreIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Content>
      <ModalDetailsService
        solicitation={selectedService}
        isOpen={displayModalDetailsEvents}
        isTutorial={runningTutorial}
        onRequestClose={() => {
          setDisplayModalDetailsEvents(false);
        }}
      />
    </Container>
  );
}
